import { useState, useCallback, useRef } from 'react'
import { kanbify } from '../lib/kanbifyClient'
import { logDevError } from '../lib/logger'

/**
 * Sanitize a user query for safe use in PostgREST .ilike() / .or() filters.
 * Escapes characters that have special meaning in LIKE patterns or PostgREST filter syntax.
 */
export function sanitizeSearchQuery(raw) {
    return raw
        .replace(/\\/g, '\\\\')  // backslash first (escape char in LIKE)
        .replace(/%/g, '\\%')    // LIKE wildcard
        .replace(/_/g, '\\_')    // LIKE single-char wildcard
        .replace(/\(/g, '\\(')   // PostgREST filter grouping
        .replace(/\)/g, '\\)')   // PostgREST filter grouping
        .replace(/\./g, '\\.')   // PostgREST nested field separator
        .replace(/\*/g, '\\*')   // PostgREST wildcard
        .replace(/,/g, '\\,')    // PostgREST OR separator inside .or()
}

/**
 * Global search hook — searches cards, boards, members and suggestions.
 *
 * - Cards: across all boards in the user's company (covers Dashboard Personal too,
 *   since it's the same `cards` table).
 * - Boards: same company.
 * - Members: same company. Returns task count per board.
 * - Suggestions: cross-tenant (suggestions are global per spec).
 */
export function useGlobalSearch(companyId) {
    const [results, setResults] = useState({ cards: [], boards: [], members: [], suggestions: [] })
    const [loading, setLoading] = useState(false)
    const debounceRef = useRef(null)

    const search = useCallback(async (query) => {
        if (!query || query.trim().length < 2 || !companyId) {
            setResults({ cards: [], boards: [], members: [], suggestions: [] })
            setLoading(false)
            return
        }

        const q = sanitizeSearchQuery(query.trim().toLowerCase())

        // Debounce 300ms
        if (debounceRef.current) clearTimeout(debounceRef.current)

        setLoading(true)

        debounceRef.current = setTimeout(async () => {
            try {
                const [cardsRes, boardsRes, membersRes, suggestionsRes] = await Promise.all([
                    // Search cards across all boards in the company
                    kanbify
                        .from('cards')
                        .select(`
                            id, title, description, list_id, is_completed, priority,
                            lists!inner ( id, name, board_id, boards!inner ( id, name, company_id ) )
                        `)
                        .eq('lists.boards.company_id', companyId)
                        .or(`title.ilike.%${q}%,description.ilike.%${q}%`)
                        .order('created_at', { ascending: false })
                        .limit(20),

                    // Search boards
                    kanbify
                        .from('boards')
                        .select('id, name, background_color')
                        .eq('company_id', companyId)
                        .ilike('name', `%${q}%`)
                        .order('position')
                        .limit(10),

                    // Search members by name or email
                    kanbify
                        .from('profiles')
                        .select('id, full_name, email, avatar_url, role, position')
                        .eq('company_id', companyId)
                        .or(`full_name.ilike.%${q}%,email.ilike.%${q}%`)
                        .order('full_name')
                        .limit(15),

                    // Search suggestions (cross-tenant — global feed)
                    kanbify
                        .from('suggestions_feed')
                        .select('id, title, description, category, status, author_name, author_company_name')
                        .or(`title.ilike.%${q}%,description.ilike.%${q}%`)
                        .order('created_at', { ascending: false })
                        .limit(10),
                ])

                const cards = (cardsRes.data || []).map((card) => ({
                    id: card.id,
                    title: card.title,
                    description: card.description,
                    is_completed: card.is_completed,
                    priority: card.priority,
                    list_name: card.lists?.name,
                    board_id: card.lists?.boards?.id,
                    board_name: card.lists?.boards?.name,
                }))

                const boards = boardsRes.data || []

                // Fetch all card_members for the matched members in a single query
                // (previously one query per member → N+1)
                const memberIds = (membersRes.data || []).map((m) => m.id)
                const countsByUser = {}
                if (memberIds.length > 0) {
                    const { data: boardCounts } = await kanbify
                        .from('card_members')
                        .select(`
                            user_id,
                            cards!inner (
                                id,
                                lists!inner (
                                    boards!inner ( id, name )
                                )
                            )
                        `)
                        .in('user_id', memberIds)

                    for (const cm of (boardCounts || [])) {
                        const board = cm.cards?.lists?.boards
                        if (!board) continue
                        if (!countsByUser[cm.user_id]) countsByUser[cm.user_id] = {}
                        const map = countsByUser[cm.user_id]
                        if (!map[board.id]) {
                            map[board.id] = { board_id: board.id, board_name: board.name, count: 0 }
                        }
                        map[board.id].count++
                    }
                }

                const members = (membersRes.data || []).map((member) => {
                    const map = countsByUser[member.id] || {}
                    const boardList = Object.values(map)
                    return {
                        ...member,
                        board_counts: boardList.sort((a, b) => b.count - a.count),
                        total_tasks: boardList.reduce((s, b) => s + b.count, 0),
                    }
                })

                const suggestions = suggestionsRes.data || []

                setResults({ cards, boards, members, suggestions })
            } catch (err) {
                logDevError('Global search error:', err)
            } finally {
                setLoading(false)
            }
        }, 300)
    }, [companyId])

    const clear = useCallback(() => {
        setResults({ cards: [], boards: [], members: [], suggestions: [] })
        setLoading(false)
        if (debounceRef.current) clearTimeout(debounceRef.current)
    }, [])

    const hasResults = results.cards.length > 0
        || results.boards.length > 0
        || results.members.length > 0
        || results.suggestions.length > 0

    return { results, loading, search, clear, hasResults }
}
